import React, { useCallback, useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { MailWarning } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import ProductsSidebar from '../components/ProductsSidebar';
import api from '../api';
import { ThemeSwitcher } from '../theme/ThemeProvider';

const DashboardLayout = () => {
    const [user, setUser] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const location = useLocation();
    const showProducts = !location.pathname.startsWith('/dashboard/admin');

    const fetchUser = useCallback(() => {
        return api.get('/api/users/me')
            .then(res => {
                setUser(res.data);
                return res.data;
            });
    }, []);

    const fetchSessions = () => {
        return api.get('/api/auth/sessions')
            .then(res => setSessions(res.data))
            .catch(() => setSessions([]));
    };

    useEffect(() => {
        Promise.all([fetchUser(), fetchSessions()])
            .then(() => setLoading(false))
            .catch(() => {
                navigate('/login');
            });
    }, [navigate, fetchUser]);

    useEffect(() => {
        if (loading) return;
        let refreshing = false;
        // Письмо обычно открывают в новой вкладке. При возвращении обновляем профиль,
        // иначе уже открытый кабинет продолжит показывать прежний статус адреса.
        const refreshOnReturn = () => {
            if (document.visibilityState === 'hidden' || refreshing) return;
            refreshing = true;
            fetchUser()
                .catch(error => {
                    if (error.response?.status === 401) navigate('/login');
                })
                .finally(() => { refreshing = false; });
        };
        window.addEventListener('focus', refreshOnReturn);
        document.addEventListener('visibilitychange', refreshOnReturn);
        return () => {
            window.removeEventListener('focus', refreshOnReturn);
            document.removeEventListener('visibilitychange', refreshOnReturn);
        };
    }, [loading, fetchUser, navigate]);

    if (loading) return <div className="flex h-screen items-center justify-center text-gray-500">Загрузка профиля...</div>;

    return (
        <div className="portal-shell flex flex-col lg:flex-row min-h-screen lg:h-screen w-full bg-gray-50 lg:overflow-hidden">
            <Sidebar user={user} sessions={sessions} />
            <main className="order-3 lg:order-2 flex-1 min-w-0 p-4 sm:p-6 xl:p-10 lg:overflow-y-auto">
                <div className="portal-toolbar"><span>АККАУНТ IT-BURGER</span><ThemeSwitcher /></div>
                <div className="w-full max-w-[800px] mx-auto">
                    {/*
                        Баннер виден на всех страницах кабинета: подтвердить владение
                        адресом нужно даже тем, кто редко открывает настройки профиля.
                    */}
                    {user && !user.emailVerified && (
                        <div className="mb-6 p-3.5 rounded-lg text-sm flex items-start gap-2.5 bg-amber-50 text-amber-900 border border-amber-200">
                            <MailWarning size={20} className="shrink-0 mt-0.5" />
                            <span>
                                Адрес <span className="font-medium">{user.email}</span> не подтверждён.
                                Подтвердите, что этот адрес принадлежит вам.{' '}
                                <Link to="/dashboard/profile" className="font-medium underline">
                                    Подтвердить
                                </Link>
                            </span>
                        </div>
                    )}
                    <Outlet context={{ user, setUser, sessions, fetchSessions }} />
                </div>
            </main>
            {showProducts && <ProductsSidebar />}
        </div>
    );
};

export default DashboardLayout;
